import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Check, Loader2, Copy } from 'lucide-react';
import SectionHead from '../SectionHead';
import { useLang } from '@/context/LanguageContext';
import ApplyStepLoan from './ApplyStepLoan';
import ApplyStepKyc from './ApplyStepKyc';
import ApplyStepReview from './ApplyStepReview';
import { buildQuote, inr, dueDate } from '@/lib/loan';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  amount: 25000,
  tenure_days: 30,
  purpose: 'Medical emergency',
  full_name: '',
  phone: '',
  email: '',
  employment_type: 'Salaried — Private',
  monthly_income: '',
  salary_date: '1',
  pan_number: '',
  aadhaar_last4: '',
  city: '',
  bank_name: '',
  account_number: '',
  ifsc: '',
  consents: [false, false, false],
};

export default function ApplyFlow() {
  const { t, locale } = useLang();
  const a = t.apply;
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [copied, setCopied] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const h = (e) => setForm((f) => ({ ...f, ...e.detail }));
    window.addEventListener('pdi-prefill', h);
    return () => window.removeEventListener('pdi-prefill', h);
  }, []);

  const validatePersonal = () => {
    const e = {};
    if (form.full_name.trim().length < 2) e.full_name = a.errors.name;
    if (!/^[6-9]\d{9}$/.test(form.phone)) e.phone = a.errors.phone;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = a.errors.email;
    if (!form.monthly_income || Number(form.monthly_income) < 1000) e.monthly_income = a.errors.income;
    if (!form.salary_date || Number(form.salary_date) < 1 || Number(form.salary_date) > 31) e.salary_date = a.errors.salaryDate;
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(form.pan_number)) e.pan_number = a.errors.pan;
    if (!/^\d{4}$/.test(form.aadhaar_last4)) e.aadhaar_last4 = a.errors.aadhaar;
    return e;
  };

  const validateBank = () => {
    const e = {};
    if (form.bank_name.trim().length < 2) e.bank_name = a.errors.bankName;
    if (!/^\d{9,18}$/.test(form.account_number)) e.account_number = a.errors.account;
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.ifsc)) e.ifsc = a.errors.ifsc;
    return e;
  };

  const next = () => {
    if (step === 1) {
      const e = validatePersonal();
      setErrors(e);
      if (Object.keys(e).length) return;
    }
    setErrors({});
    setStep((s) => s + 1);
  };

  const back = () => {
    setErrors({});
    setStep((s) => s - 1);
  };

  const submit = async () => {
    const e = validateBank();
    if (!form.consents.every(Boolean)) e.consents = a.errors.consents;
    setErrors(e);
    if (Object.keys(e).length) return;

    setSubmitting(true);
    setSubmitError('');
    try {
      const { data } = await axios.post(`${API}/loans/apply`, {
        full_name: form.full_name,
        phone: form.phone,
        email: form.email,
        amount: Number(form.amount),
        tenure_days: Number(form.tenure_days),
        purpose: form.purpose,
        employment_type: form.employment_type,
        monthly_income: Number(form.monthly_income),
        salary_date: Number(form.salary_date),
        pan_number: form.pan_number,
        aadhaar_last4: form.aadhaar_last4,
        city: form.city || null,
        bank_name: form.bank_name,
        account_number: form.account_number,
        ifsc: form.ifsc,
        consent_terms: form.consents[0],
        consent_rbi: form.consents[1],
        consent_data: form.consents[2],
      });
      setResult(data);
    } catch (err) {
      setSubmitError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(initialForm);
    setStep(0);
    setResult(null);
    setErrors({});
  };

  const quote = buildQuote(form.amount, form.tenure_days);

  const steps = a.steps;
  const btnBack =
    'flex items-center gap-2 rounded-xl border border-white/15 px-6 py-3 text-sm font-bold text-slate-300 transition-colors hover:border-pulse/50 hover:text-pulse';
  const btnNext =
    'group flex flex-1 items-center justify-center gap-2 rounded-xl bg-pulse py-3.5 font-display font-bold text-ink transition-all hover:bg-pulse-dim';

  return (
    <section id="apply" className="relative overflow-hidden bg-abyss/40 py-24 lg:py-32" data-testid="apply-section">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-pulse/5 blur-[130px]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow={a.eyebrow} title={a.title} sub={a.sub} />

        <div className="mx-auto mt-14 max-w-3xl">
          {!result ? (
            <div className="rounded-3xl border border-white/10 bg-panel/90 p-6 shadow-2xl backdrop-blur-xl sm:p-10" data-testid="apply-form-card">
              <div className="mb-9 flex items-center justify-between" data-testid="apply-step-indicator">
                {steps.map((label, i) => (
                  <div key={label} className="flex items-center gap-2.5 sm:gap-3">
                    <span
                      data-testid={`apply-step-dot-${i + 1}`}
                      className={`flex h-8 w-8 items-center justify-center rounded-full border font-mono text-xs font-bold transition-colors ${
                        i < step
                          ? 'border-pulse bg-pulse text-ink'
                          : i === step
                            ? 'border-pulse text-pulse'
                            : 'border-white/15 text-slate-600'
                      }`}
                    >
                      {i < step ? <Check size={14} /> : i + 1}
                    </span>
                    <span
                      className={`hidden text-xs font-semibold sm:block ${
                        i === step ? 'text-white' : 'text-slate-600'
                      }`}
                    >
                      {label}
                    </span>
                    {i < steps.length - 1 && <span className="mx-1 h-px w-6 bg-white/10 sm:w-10" />}
                  </div>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                >
                  {step === 0 && (
                    <ApplyStepLoan a={a.step1} form={form} set={set} locale={locale} errors={errors} onNext={next} />
                  )}
                  {step === 1 && <ApplyStepKyc a={a.step2} form={form} set={set} errors={errors} />}
                  {step === 2 && (
                    <ApplyStepReview
                      a={a.step3}
                      t={t}
                      form={form}
                      set={set}
                      errors={errors}
                      onEditLoan={() => setStep(0)}
                      onEditPersonal={() => setStep(1)}
                    />
                  )}
                </motion.div>
              </AnimatePresence>

              {step > 0 && (
                <div className="mt-8 flex items-center gap-3">
                  <button type="button" data-testid={`apply-back-step-${step + 1}`} onClick={back} className={btnBack}>
                    <ArrowLeft size={16} /> {a.step2.back}
                  </button>
                  {step < 2 ? (
                    <button type="button" data-testid={`apply-next-step-${step + 1}`} onClick={next} className={btnNext}>
                      {a.step2.next} <ArrowRight size={17} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid="apply-submit-button"
                      onClick={submit}
                      disabled={submitting}
                      className={`${btnNext} disabled:opacity-60`}
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={17} className="animate-spin" /> {a.step3.submitting}
                        </>
                      ) : (
                        <>
                          {a.step3.submit} <ArrowRight size={17} />
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}

              {step === 2 && (
                <div className="mt-4 flex items-center justify-between rounded-xl border border-gold/30 bg-gold/5 px-5 py-3" data-testid="apply-review-total">
                  <span className="text-sm font-semibold text-slate-300">{a.step1.total}</span>
                  <span className="font-mono text-lg font-bold text-gold">{inr(quote.total_repayment)}</span>
                </div>
              )}
              {submitError && (
                <p data-testid="apply-submit-error" className="mt-4 text-center text-sm font-medium text-red-400">
                  {submitError}
                </p>
              )}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="rounded-3xl border border-pulse/30 bg-panel/90 p-8 text-center shadow-2xl backdrop-blur-xl sm:p-12"
              data-testid="apply-success-card"
            >
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 14, delay: 0.15 }}
                className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-pulse text-ink glow-pulse"
                data-testid="success-check"
              >
                <Check size={30} strokeWidth={3} />
              </motion.span>
              <h3 className="mt-6 font-display text-2xl font-extrabold text-white sm:text-3xl">{a.success.title}</h3>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-400">{a.success.sub}</p>

              <button
                type="button"
                data-testid="success-reference-id"
                onClick={() => {
                  navigator.clipboard?.writeText(result.reference_id);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
                className="mx-auto mt-7 flex items-center gap-3 rounded-xl border border-pulse/30 bg-ink/70 px-6 py-4 font-mono text-2xl font-bold text-pulse transition-colors hover:bg-ink"
              >
                {result.reference_id} <Copy size={18} className="text-slate-500" />
              </button>
              <p className="mt-2 text-[11px] uppercase tracking-widest text-slate-500">
                {copied ? 'Copied!' : a.success.ref}
              </p>

              <div className="mx-auto mt-8 grid max-w-md gap-2 text-sm">
                <div className="flex justify-between rounded-lg bg-ink/50 px-4 py-2.5" data-testid="success-status">
                  <span className="text-slate-400">{a.success.status}</span>
                  <span className="font-mono font-semibold uppercase text-pulse">{result.status}</span>
                </div>
                <div className="flex justify-between rounded-lg bg-ink/50 px-4 py-2.5" data-testid="success-repayment">
                  <span className="text-slate-400">{a.step1.total}</span>
                  <span className="font-mono font-semibold text-white">{inr(result.quote.total_repayment)}</span>
                </div>
                <div className="flex justify-between rounded-lg bg-ink/50 px-4 py-2.5" data-testid="success-disbursal">
                  <span className="text-slate-400">{a.success.disbursal}</span>
                  <span className="font-mono font-semibold text-white">{a.success.disbursalVal}</span>
                </div>
              </div>

              <p className="mt-6 text-xs text-slate-500" data-testid="success-notified">{a.success.notified}</p>
              <button
                type="button"
                data-testid="success-track-button"
                onClick={() => window.location.assign(`/track?ref=${result.reference_id}`)}
                className="mt-4 w-full rounded-xl border border-pulse/30 bg-pulse/5 py-3 text-sm font-bold text-pulse transition-colors hover:bg-pulse hover:text-ink"
              >
                {t.track.trackCta}
              </button>
              <button
                type="button"
                data-testid="success-new-application-button"
                onClick={reset}
                className="mt-8 rounded-full border border-white/15 px-7 py-3 text-sm font-bold text-slate-200 transition-colors hover:border-pulse/50 hover:text-pulse"
              >
                {a.success.new}
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
